/**
 * Brand Kit API Service
 *
 * Proxies requests to the main app's API to fetch brand kit data.
 * Since the marketing site is separate, it needs to call the app's API.
 */

// Use api.rooflyzer.pro for brand kit API calls (app-edge worker)
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.rooflyzer.pro/v1';

export interface PublicBrandKit {
  id: string;
  slug: string;
  company_name: string;
  tagline?: string;
  logo_url?: string;
  hero_image_url?: string;
  phone?: string;
  email?: string;
  website?: string;
  city?: string;
  state?: string;
  zip?: string;
  address?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  about?: string;
  years_in_business?: number;
  license_number?: string;
  insurance_info?: string;
  google_rating?: number;
  google_place_id?: string;
  services?: Array<{
    id: string;
    name: string;
    description?: string;
    icon?: string;
  }>;
  service_areas?: Array<{
    id: string;
    city: string;
    state: string;
    zip?: string;
    is_primary: boolean;
  }>;
  certifications?: Array<{
    id: string;
    name: string;
    issuer?: string;
    issue_date?: string;
    expiry_date?: string;
    certification_number?: string;
    logo_url?: string;
  }>;
  reviews?: Array<{
    id: string;
    author_name: string;
    rating: number;
    text: string;
    review_date: string;
    platform: string;
    profile_url?: string;
  }>;
  project_photos?: Array<{
    id: string;
    url: string;
    caption?: string;
  }>;
  social_links?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
  };
}

export interface BrandKitListItem {
  id: string;
  slug: string;
  company_name: string;
  logo_url?: string;
  tagline?: string;
  city?: string;
  state?: string;
  primary_color?: string;
}

/**
 * Get a public brand kit by slug
 * Proxies to the main app's API
 */
export async function getBrandKitBySlug(slug: string): Promise<PublicBrandKit | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/public/brand-kits/${slug}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch brand kit: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching brand kit:', error);
    throw error;
  }
}

/**
 * Get all public brand kits for the directory
 * Proxies to the main app's API
 */
export async function getAllBrandKits(): Promise<BrandKitListItem[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/public/brand-kits`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch brand kits: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching brand kits:', error);
    throw error;
  }
}

/**
 * Search brand kits by query
 * Proxies to the main app's API
 */
export async function searchBrandKits(query: string): Promise<BrandKitListItem[]> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/public/brand-kits/search?q=${encodeURIComponent(query)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to search brand kits: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching brand kits:', error);
    throw error;
  }
}
